import React, { useEffect, useState, useMemo, useRef } from "react";
import MapGL, { Source, Layer } from "react-map-gl";
import { json as requestJson } from "d3-request";
import { Box } from "@material-ui/core";

import { natcatLayersEnum } from "./NatcatAnalyticsConstants";
import { MapLayersSelector } from "../map/MapLayerSelector";
import { lightMapStye, defaultRomeLatitude, defaultRomeLongitude } from "../map/Constants";
import { natcatAnalyticsLayerId } from "./NatcatMapFillLayers";
import { Marker } from "react-map-gl";
import { ScaleBar } from "../map/ScaleBar";
import Pin from "../map/pin";
import { natcatEuroText } from "./../legends/LegendUtils";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export const NatcatAnalyticsMap = () => {
  const mapRef = useRef();

  const [viewState, setViewState] = useState({
    latitude: defaultRomeLatitude,
    longitude: defaultRomeLongitude,
    zoom: 6,
  });

  const [mapStyle, setMapStyle] = useState(lightMapStye);
  const [geoJson, setGeoJson] = useState("");

  const [currentNatcatIndex, setCurrentNatcatIndex] = useState(natcatLayersEnum().noHAzardOrKpi.id);
  const [currentNatcatLayer, setCurrentNatcatLayer] = useState(natcatLayersEnum().noHAzardOrKpi);
  const [userMapLayers, setUserMapLayers] = useState({ noHAzardOrKpi: natcatLayersEnum().noHAzardOrKpi });
  const [longitude, setLongitude] = useState(0);
  const [latitude, setLatitude] = useState(0);
  const [selectedProperties, setSelectedProperties] = useState(null);

  // const [currentNatcatHazardIndex, setCurrentNatcatHazardIndex] = useState(
  //   natcatHazardLayersEnum().noHazard.id,
  // );
  // const [currentNatcatKPIIndex, setCurrentNatcatKPIIndex] = useState(natcatKPILayersEnum().noKpi.id);

  // useEffect(() => {
  //   const currentHazardLayer = natcatHazardLayersEnum()[currentNatcatHazardIndex];
  //   setMapStyle(currentHazardLayer.mapLayer);
  //   setCurrentLegendComponent(currentHazardLayer.legend);
  // }, [currentNatcatHazardIndex]);

  // useEffect(() => {
  //   const currentKPIGeojsonUrl = natcatKPILayersEnum()[currentNatcatKPIIndex].geojsonUrl;
  //   requestJson(currentKPIGeojsonUrl, (err, response) => {
  //     if (err) {
  //       console.error("==error==", err);
  //     }
  //     setGeoJson(response);
  //   });
  // }, [currentNatcatKPIIndex]);

  useEffect(() => {
    if (!currentNatcatIndex) return;

    const currentLayer = natcatLayersEnum()[currentNatcatIndex];
    setCurrentNatcatLayer(currentLayer);

    if (currentLayer.mapLayer && currentLayer.geojsonUrl) {
      requestJson(currentLayer.geojsonUrl, (err, response) => {
        if (err) {
          console.error("==mapLayer and geojson error==", err);
        }
        setMapStyle(currentLayer.mapLayer);
        setGeoJson(response);
      });
    } else if (currentLayer.mapLayer) {
      // hazard selected
      setMapStyle(currentLayer.mapLayer);
    } else if (currentLayer.geojsonUrl) {
      // kpi selected
      requestJson(currentLayer.geojsonUrl, (err, response) => {
        if (err) {
          console.error("==geojson error==", err);
        }
        setMapStyle(lightMapStye);
        setGeoJson(response);
      });
    }
  }, [currentNatcatIndex]);

  const data = useMemo(() => {
    return geoJson;
  }, [geoJson]);

  const onMove = (viewState) => {
    setViewState(viewState);
  };

  const onClick = (event) => {
    setLongitude(event.lngLat.lng);
    setLatitude(event.lngLat.lat);

    const { features } = event;
    const selectedFeature = features && features[0];

    if (selectedFeature) {
      setSelectedProperties(selectedFeature.properties);
    } else {
      setSelectedProperties(null);
    }
  };

  const getPinExplanation = () => {
    const label = natcatLayersEnum()[currentNatcatIndex].label;
    // if TIV is substring of label
    if (!(label.includes("TIV") || label.includes("Weighted")))
      return "Select a map with TIV or Weighted to see location details.";
    if (!selectedProperties)
      return `Click on the map to see the Total Insured Value (TIV) or Weighted value of the selected area.`;
    const prop = natcatLayersEnum()[currentNatcatIndex].geojsonProp;
    if (!prop) {
      console.error("==missing prop== in ", natcatLayersEnum()[currentNatcatIndex]);
      return `Internal error: missing property in ${label}.`;
    }
    if (!(prop in selectedProperties)) return `Click on the map.`;

    let area;
    if (label.includes("Hex 7")) area = "a hexagon of 5 km²";
    else if (label.includes("Hex 6")) area = "a hexagon of 35 km²";
    else area = `comune ${selectedProperties.COMUNE}`;

    const latLonText = ` ( ${latitude.toFixed(3)},  ${longitude.toFixed(3)} )`;

    if (label.includes("TIV"))
      return `Explanation: In ${label} you selected ${latLonText} - ${area} where the Total Insured Value (TIV) of propetries with high risk is ${selectedProperties[prop]} millions of ${natcatEuroText}.`;
    if (label.includes("Weighted"))
      return `Explanation: In ${label} you selected ${latLonText} - ${area} where the weighted risk is ${selectedProperties[
        prop
      ].toFixed(3)} (of 1..10) based on data from ${selectedProperties["policies_count"]} contracts .`;
  };

  return (
    <>
      <MapGL
        {...viewState}
        ref={mapRef}
        mapboxAccessToken={TOKEN}
        style={{ width: "100%", height: "600px" }}
        onMove={(evt) => onMove(evt.viewState)}
        interactiveLayerIds={(data && currentNatcatLayer.geojsonUrl && [natcatAnalyticsLayerId]) || []}
        mapStyle={mapStyle}
        onClick={onClick}
      >
        {data && currentNatcatLayer.geojsonUrl && (
          <>
            {/* Design note: placing them after reach other reduces console warnings */}
            <Source id="natcat-src-id" type="geojson" data={data}></Source>
            <Layer
              id="natcat-layer-id"
              source="natcat-src-id"
              {...natcatLayersEnum()[currentNatcatIndex].mapGeojsonLayer}
            />
          </>
        )}

        {natcatLayersEnum()[currentNatcatIndex].legend && natcatLayersEnum()[currentNatcatIndex].legend}
        {natcatLayersEnum()[currentNatcatIndex].secondLegend &&
          natcatLayersEnum()[currentNatcatIndex].secondLegend}

        <MapLayersSelector
          currentIndex={currentNatcatIndex}
          setCurrentIndex={setCurrentNatcatIndex}
          layersList={natcatLayersEnum()}
        />

        {latitude && longitude && (
          <Marker longitude={longitude} latitude={latitude}>
            <Pin size={20} />
          </Marker>
        )}

        {/* <MapLayersSelector
        currentIndex={currentNatcatHazardIndex}
        setCurrentIndex={setCurrentNatcatHazardIndex}
        layersList={natcatLayersEnum()}
      /> */}

        {/* <MapLayersSelector
        currentIndex={currentNatcatKPIIndex}
        setCurrentIndex={setCurrentNatcatKPIIndex}
        layersList={natcatKPILayersEnum()}
        topPx={300}
      /> */}
        <ScaleBar zoom={viewState.zoom} />
      </MapGL>

      <Box mt={2} mb={1}>
        {getPinExplanation()}
      </Box>
    </>
  );
};
